/**
 * EmailInsightsModule — root orchestrator component.
 * Self-contained: manages all state, data fetching, and rendering.
 * Host app mounts this at /dashboard/email-insights.
 */

import React, { useCallback, useRef, useState } from 'react';
import { ConfigProvider, Alert, Button, Typography, Tabs } from 'antd';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';

import { getEmailInsightsTheme } from './theme/antdTheme';
import { useGmailAuth } from './hooks/useGmailAuth';
import { useEmailFetch } from './hooks/useEmailFetch';
import { useMetrics } from './hooks/useMetrics';
// AI insights removed 2026-08-13 — the browser-side DeepSeek call sent Gmail
// contact addresses to a third party, which contradicted Privacy §3.2 and
// blocked Google OAuth verification. Subject-line analysis was removed with it;
// the Subject header is never requested (see engine/gmailClient.js).
import { useTeamInsights } from './hooks/useTeamInsights';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

import { ConnectGmail } from './components/ConnectGmail';
import { ControlsBar } from './components/ControlsBar';
import { FetchProgress } from './components/FetchProgress';
import { ReadyToAnalyze } from './components/ReadyToAnalyze';
import { StatsRow } from './components/StatsRow';
import { Charts } from './components/Charts';
import { ContactTables } from './components/ContactTables';
import { DelayedEmailsPanel } from './components/DelayedEmailsPanel';
import { HeatmapChart } from './components/HeatmapChart';
import { DayOfWeekChart } from './components/DayOfWeekChart';
import { EmailDebtPanel } from './components/EmailDebtPanel';
import { ResponseRatePanel } from './components/ResponseRatePanel';
import { CommunicationLoopsPanel } from './components/CommunicationLoopsPanel';
import { TeamInsightsTab } from './components/TeamInsightsTab';

import { FADE_IN, DEFAULT_DELAY_THRESHOLD, DEFAULT_DAYS } from './constants';
import dayjs from 'dayjs';
import { Mail, Users } from 'lucide-react';
import { PageShell } from '../../pages/DashboardV2/components/PageShell';
import { NEW_UI } from '../../config/featureFlags';

const { Title, Text } = Typography;

// True when mounted inside the new V2 shell — used to render the standard V2
// PageShell header instead of the legacy inline header. When NEW_UI is on, the
// V2 shell owns /dashboard, so this module is always V2 there; otherwise V2 is
// the /dashboard/v1 mount.
const isV2Shell = () =>
  typeof window !== 'undefined' &&
  (NEW_UI || window.location.pathname.startsWith('/dashboard/v1'));

export function EmailInsightsModule() {
  const reduced = useReducedMotion();
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  const v2 = isV2Shell();

  // ── State layers ──────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('my'); // controlled tab (for V2 PageShell)
  const [delayThreshold, setDelayThreshold] = useState(DEFAULT_DELAY_THRESHOLD);
  const [delayedModalOpen, setDelayedModalOpen] = useState(false);
  const [debtModalOpen, setDebtModalOpen] = useState(false);
  const [loopsModalOpen, setLoopsModalOpen] = useState(false);

  const auth = useGmailAuth();
  const emailFetch = useEmailFetch();
  const metrics = useMetrics(emailFetch.data);
  const { user } = useAuth();
  const isOrgAdmin = user?.role === 'org_admin';
  const teamInsights = useTeamInsights('reportees', true);
  // ADDITIVE: org_admin also loads org-wide member list for Team Insights sub-tab
  const orgWideInsights = useTeamInsights('org', isOrgAdmin);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const rangeRef = useRef([dayjs().subtract(DEFAULT_DAYS, 'day'), dayjs()]);
  const [rangeDays, setRangeDays] = useState(DEFAULT_DAYS);
  const handleRangeChange = useCallback((next) => {
    rangeRef.current = next;
    const [from, to] = next || [];
    if (from && to) setRangeDays(Math.max(1, to.diff(from, 'day')));
  }, []);

  const handleAnalyze = useCallback(async (from, to) => {
    if (!auth.isConnected) return;
    const res = await emailFetch.fetch(from, to);
    // 410 from proxy — grant dead. Drop Connected UI and show Connect Gmail.
    if (res?.tokenExpired) {
      auth.markExpired();
      emailFetch.reset();
    }
  }, [auth, emailFetch]);

  const handleDisconnect = useCallback(() => {
    auth.disconnect();
    emailFetch.reset();
  }, [auth, emailFetch]);

  // Team tab: reportees exist OR org_admin (Org-wide even with zero reportees)
  const showTeamTab =
    isOrgAdmin ||
    teamInsights.loading ||
    teamInsights.team.length > 0;

  const teamTabProps = {
    team: teamInsights.team,
    isDark,
    loading: teamInsights.loading,
    page: teamInsights.page,
    pageSize: teamInsights.pageSize,
    total: teamInsights.total,
    totalPages: teamInsights.totalPages,
    onPageChange: teamInsights.goToPage,
    search: teamInsights.search,
    setSearch: teamInsights.setSearch,
    isOrgAdmin,
    orgWide: isOrgAdmin ? orgWideInsights : null,
  };

  const myInsightsContent = (
    <AnimatePresence mode="wait">
      {/* ── Not connected ── */}
      {!auth.isConnected && (
        <motion.div key="connect" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <ConnectGmail
            onConnect={auth.connect}
            loading={auth.loading}
            error={auth.error}
            isDark={isDark}
          />
        </motion.div>
      )}

      {/* ── Connected ── */}
      {auth.isConnected && (
        <motion.div key="dashboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          {auth.needsFullAccess && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16, borderRadius: 10 }}
              message="Reconnect Gmail to upgrade inbox access"
              description="One-time reconnect so TeamGrid can keep a full-inbox grant. This page will not show email content."
              action={
                <Button type="primary" size="small" onClick={auth.connect} style={{ borderRadius: 8 }}>
                  Reconnect
                </Button>
              }
            />
          )}
          <ControlsBar
            onAnalyze={handleAnalyze}
            onDisconnect={handleDisconnect}
            loading={emailFetch.loading}
            hasData={!!metrics}
            isDark={isDark}
            onRangeChange={handleRangeChange}
          />

          {emailFetch.error && !emailFetch.loading && (
            <Alert
              type="error"
              message="Failed to fetch emails"
              description={emailFetch.error}
              showIcon
              closable
              style={{ marginBottom: 20, borderRadius: 10 }}
            />
          )}

          {emailFetch.loading && (
            <FetchProgress
              fetched={emailFetch.progress.fetched}
              total={emailFetch.progress.total}
              isDark={isDark}
            />
          )}

          {!emailFetch.loading && metrics && (
            <>
              {metrics.totalReceived === 0 && metrics.totalSent === 0 ? (
                <Alert
                  type="info"
                  message="No emails found for the selected date range."
                  description="Try selecting a wider date range or check that your Gmail account has activity in this period."
                  showIcon
                  style={{ borderRadius: 10, marginBottom: 20 }}
                />
              ) : (
                <>
                  <StatsRow
                    metrics={metrics}
                    isDark={isDark}
                    delayThreshold={delayThreshold}
                    onDelayedClick={() => setDelayedModalOpen(true)}
                    onDebtClick={() => setDebtModalOpen(true)}
                    onLoopsClick={() => setLoopsModalOpen(true)}
                  />
                  <Charts metrics={metrics} isDark={isDark} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
                    <HeatmapChart metrics={metrics} isDark={isDark} />
                    <DayOfWeekChart metrics={metrics} isDark={isDark} />
                  </div>
                  <ContactTables metrics={metrics} isDark={isDark} />
                  <ResponseRatePanel metrics={metrics} isDark={isDark} />
                  <CommunicationLoopsPanel
                    metrics={metrics}
                    isDark={isDark}
                    externalOpen={loopsModalOpen}
                    onExternalClose={() => setLoopsModalOpen(false)}

                  />
                  <EmailDebtPanel
                    metrics={metrics}
                    isDark={isDark}
                    externalOpen={debtModalOpen}
                    onExternalClose={() => setDebtModalOpen(false)}
                  />
                  <DelayedEmailsPanel
                    metrics={metrics}
                    isDark={isDark}
                    onThresholdChange={setDelayThreshold}
                    open={delayedModalOpen}
                    onOpen={() => setDelayedModalOpen(true)}
                    onClose={() => setDelayedModalOpen(false)}
                  />
                </>
              )}
            </>
          )}

          {!emailFetch.loading && !metrics && !emailFetch.error && (
            <ReadyToAnalyze
              isDark={isDark}
              rangeDays={rangeDays}
              onAnalyze={() => {
                const [from, to] = rangeRef.current || [];
                if (!from || !to) return;
                handleAnalyze(from.toDate(), to.toDate());
              }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  const tabItems = [
    {
      key: 'my',
      label: 'My Email Insights',
      children: myInsightsContent,
    },
    ...(showTeamTab ? [{
      key: 'team',
      label: teamInsights.loading ? 'Team Insights' : `Team Insights (${isOrgAdmin ? Math.max(teamInsights.total, orgWideInsights.total) : teamInsights.total})`,
      children: <TeamInsightsTab {...teamTabProps} />,
    }] : []),
  ];

  // ── V2 shell: standard PageShell header + flush animated tabs ──────────────
  if (v2) {
    const v2Tabs = [
      { key: 'my', label: 'My Email Insights', icon: <Mail size={15} /> },
      ...(showTeamTab ? [{
        key: 'team',
        label: 'Team Insights',
        icon: <Users size={15} />,
        count: teamInsights.loading && (!isOrgAdmin || orgWideInsights.loading)
          ? undefined
          : (isOrgAdmin ? Math.max(teamInsights.total, orgWideInsights.total) : teamInsights.total),
      }] : []),
    ];
    const current = activeTab === 'team' && showTeamTab ? 'team' : 'my';
    const activeContent = current === 'team'
      ? <TeamInsightsTab {...teamTabProps} />
      : myInsightsContent;

    return (
      <ConfigProvider theme={getEmailInsightsTheme(isDark)}>
        <PageShell
          eyebrow="AI · Communication"
          title="Email Insights"
          subtitle="Understand your Gmail communication patterns with AI-powered analysis."
          icon={<Mail size={22} />}
          tabs={showTeamTab ? v2Tabs : undefined}
          activeTab={current}
          onTabChange={setActiveTab}
          tabLayoutId="emailShellTab"
        >
          <div style={{ padding: '4px 4px 24px' }}>
            {activeContent}
          </div>
        </PageShell>
      </ConfigProvider>
    );
  }

  // ── Legacy shell: original inline header (unchanged) ───────────────────────
  return (
    <ConfigProvider theme={getEmailInsightsTheme(isDark)}>
      <div style={{ padding: '24px 28px', minHeight: '100vh' }}>
        {/* Page header */}
        <motion.div
          variants={reduced ? {} : FADE_IN}
          initial="hidden"
          animate="visible"
          style={{ marginBottom: 24 }}
        >
          <Title level={3} style={{ margin: 0, fontWeight: 700 }}>Email Insights</Title>
          <Text type="secondary">Understand your Gmail communication patterns with AI-powered analysis.</Text>
        </motion.div>

        {showTeamTab ? (
          <Tabs items={tabItems} defaultActiveKey="my" />
        ) : (
          myInsightsContent
        )}
      </div>
    </ConfigProvider>
  );
}
